import { GuestDetails } from "@/api/register";

import WhiteLabelLogo from "@/components/_module/loading/white-label-logo";
import CardFailedPage from "@/components/pages/card/failed";
import CardSuccessPage from "@/components/pages/card/success";
import useAddCreditCard from "@/features/credit-cards/useAddCreditCard";
import {
	PaymentDetails,
	useSubmitPayment,
} from "@/features/payment-credentials/useSubmitPayment";
import { Progress } from "@/components/ui/progress";
import React, { useEffect, useRef, useState } from "react";
import { z } from "zod";
import SubmitButton from "../../../../../../components/ui/submit-button";
import Typography from "../../../../../../components/ui/typography";
import { BANK_ACCOUNT_STATEMENT } from "@/constants/common-constants";
import { doesGuestNameMatchCardholder } from "@/lib/name-matching/name-matching.utils";
import { captureMessage } from "@sentry/react";
import { LockIcon } from "lucide-react";

interface SoftpointIframeProps {
	user: GuestDetails;
	iframeAccessToken: string;
	locationId: number;
}

const EventTypesSchema = z.union([
	z.literal("spi_submit_approved"),
	z.literal("spi_submit_error"),
	z.literal("spi_submit_declined"),
	z.literal("spi_submit_decline"),
	z.literal("spi_iframe_loaded"),
]);

export type EventTypes = z.infer<typeof EventTypesSchema>;

const PaymentEventSchema = z.object({
	reference_id: z.string(),
	currency_iso: z.string().length(3, "Must be 3-letter ISO currency code"),
	authcode: z.string(),
	transaction_id: z.string().uuid("Must be a valid UUID"),
	card_data: z.object({
		token: z.string(),
		cardfullname: z.string(),
		expiry_information: z.string(),
		last4: z.string().length(4, "Must be exactly 4 digits"),
		card_brand: z.string(),
	}),
	billing_address: z.object({
		street: z.string(),
		street_2: z.string(),
		zip: z.string(),
		city: z.string(),
		state: z.string(),
		country_iso: z.string(),
		email: z.string(),
	}),
});

// Type inference
type PaymentEvent = z.infer<typeof PaymentEventSchema>;

// Example usage:
const validatePaymentEvent = (data: unknown): PaymentEvent => {
	return PaymentEventSchema.parse(data);
};

export { PaymentEventSchema, validatePaymentEvent, type PaymentEvent };

export function getSoftpointIframeDomain() {
	const apiUrl = import.meta.env.VITE_API_BASE_URL;

	if (apiUrl.includes("dev")) {
		return "https://iframe-gtw.tabpoint.us";
	}
	if (apiUrl.includes("stage")) {
		return "https://stage-iframe-gtw.softpoint.io";
	}
	if (apiUrl.includes("sandbox")) {
		return "https://sandbox-iframe-gtw.softpoint.io";
	}

	return "https://iframe-gtw.softpoint.io";
}

export const getSPIframeEventType = (event: unknown): string => {
	const result = z
		.object({
			data: z.object({
				type: z.string(),
			}),
		})
		.safeParse(event);
	return result.success ? result.data.data.type : "";
};

export const getSPIframeErrorMessage = (event: unknown): string => {
	const result = z
		.object({
			data: z.object({
				message: z.object({
					data: z.object({
						error: z.object({
							message: z.string(),
						}),
					}),
				}),
			}),
		})
		.safeParse(event);
	return result.success ? result.data.data.message.data.error.message : "";
};

const DEFAULT_IFRAME_HEIGHT_PX = 416; // 26rem

export const getSPIframeHeight = (event: unknown): number | null => {
	const result = z
		.object({
			data: z.object({
				type: z.literal("sp_pay_increase_height"),
				height: z.union([z.number(), z.string()]),
			}),
		})
		.safeParse(event);
	if (!result.success) return null;

	const height = result.data.data.height;
	const parsed =
		typeof height === "number" ? height : Number.parseInt(height, 10);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const SoftpointIframe: React.FC<SoftpointIframeProps> = ({
	user,
	iframeAccessToken,
	locationId,
}) => {
	const postCardMutation = useAddCreditCard(user.id);
	const submitPaymentMutation = useSubmitPayment();
	const [isAddCreditCardIframeError, setIsAddCreditCardIframeError] =
		useState(false);
	const initalizedRef = useRef(false);
	const [loadingIframe, setLoadingIframe] = useState(true);
	const [cardError, setCardError] = useState("");
	const [iframeHeight, setIframeHeight] = useState(DEFAULT_IFRAME_HEIGHT_PX);
	React.useEffect(() => {
		if (initalizedRef.current) {
			return;
		}
		initalizedRef.current = true;

		setupIframe();
		return () => {
			removeIframe();
		};
	}, []);

	useEffect(() => {
		function onPaymentEvent(event: unknown) {
			handlePaymentEvent(event);
		}

		window.addEventListener("message", onPaymentEvent);
		return () => {
			window.removeEventListener("message", onPaymentEvent);
		};
	}, []);

	function removeIframe() {
		const iframe = document.getElementById("SPPaymentIframe");
		const existingScript = document.getElementById("softPointIFrameScript");
		const existingFunctionsScript = document.getElementById("spiHandlerScript");

		if (iframe) {
			document.body.removeChild(iframe);
		}
		if (existingScript) {
			document.body.removeChild(existingScript);
		}
		if (existingFunctionsScript) {
			document.body.removeChild(existingFunctionsScript);
		}
	}

	function setupIframe() {
		const conf: object = {
			location_id: locationId,
			access_token: iframeAccessToken,
			"include-cardholder": 2,
			"include-email": 2,
			"include-street": 2,
			"include-street2": 1,
			"include-city": 2,
			"include-state": 2,
			"include-country": 0,
			"include-zip": 2,
			"store-card": 1,
			"theme-mode": undefined,
			user_id: user.id,
			// "credit-card-only": 1,
			"validate-address": 1,
			country_iso: "US",
			currency_iso: "USD",
		};

		const domain = getSoftpointIframeDomain();
		const url = `${domain}/api/softpoint-pay`;

		const _script = document.createElement("script");
		_script.id = "softPointIFrameScript";
		_script.src = url;
		_script.async = true;
		_script.type = "text/JavaScript";

		const _script_functions = document.createElement("script");
		_script_functions.id = "spiHandlerScript";
		_script_functions.async = true;
		_script_functions.type = "text/JavaScript";
		_script_functions.innerHTML = `
		const onApprovedCallback    = (e) => { window.postMessage({ message: {eventType: 'spi_submit_approved', data: e}}, '*') }
		const onDeclinedCallback    = (e) => { window.postMessage({ message: {eventType: 'spi_submit_declined', data: e}}, '*') }
		const onErrorCallback       = (e) => { window.postMessage({ message: {eventType: 'spi_submit_error'   , data: e}}, '*') }
		const onDecline             = (e) => { window.postMessage({ message: {eventType: 'spi_submit_decline' , data: e}}, '*') }
		const onIframeLoaded        = (e) => { window.postMessage({ message: {eventType: 'spi_iframe_loaded' , data: e}}, '*') }
	  `;

		document.body.appendChild(_script);
		document.body.appendChild(_script_functions);

		_script.onload = () => {
			// @ts-ignore
			if (typeof window.initPaymentForm === "function") {
				// @ts-ignore
				window.initPaymentForm("iframeContent", conf);
			}
		};
	}

	function handleAddCreditCard(paymentEvent: PaymentEvent) {
		const {
			card_data: { card_brand, cardfullname, expiry_information, last4, token },
			transaction_id,
			billing_address,
		} = paymentEvent;

		const cardholderMatchesGuest = doesGuestNameMatchCardholder(
			user.first_name,
			user.last_name,
			cardfullname,
			user.business_name,
		);

		if (!cardholderMatchesGuest) {
			captureMessage("Cardholder name mismatch — potential fraud", {
				level: "fatal",
				tags: {
					fraud: "true",
					feature: "add_credit_card",
					signal: "cardholder_name_mismatch",
				},
				fingerprint: ["fraud", "cardholder-name-mismatch"],
				extra: {
					guestId: user.id,
					guestFirstName: user.first_name,
					guestLastName: user.last_name,
					guestBusinessName: user.business_name,
					cardholderName: cardfullname,
					locationId,
					transactionId: transaction_id,
					cardLast4: last4,
				},
			});
			setCardError(
				"The cardholder name does not match your Kashu account name or business name",
			);
			setIsAddCreditCardIframeError(true);
			return;
		}

		const expirationYear = expiry_information.split("/")[1];
		const expirationMonth = expiry_information.split("/")[0];

		postCardMutation.mutate({
			cc_token: token,
			card_type: card_brand.toUpperCase(),
			card_holder: cardfullname,
			last4: last4,
			exp_month: expirationMonth.slice(-2),
			guestId: user.id,
			locationId: locationId,
			exp_year: expirationYear.slice(-2),
			transaction_no: transaction_id,
			flow: "profile",
			reusable: 1,
			default: true,
			cvv: "",
			billing_address,
		});
	}

	function handlePaymentEvent(event: unknown) {
		try {
			const eventType = EventTypesSchema.safeParse(
				// @ts-expect-error
				event?.data?.message?.eventType,
			);

			const iframeEventType = getSPIframeEventType(event);
			if (iframeEventType === "iframeLoaded") {
				setLoadingIframe(false);
			}

			if (iframeEventType === "sp_pay_increase_height") {
				const height = getSPIframeHeight(event);
				if (height) setIframeHeight(height);
			}

			if (eventType.success && eventType.data === "spi_submit_approved") {
				try {
					// @ts-expect-error
					console.log(event?.data?.message?.data);
					const paymentEvent = PaymentEventSchema.parse(
						// @ts-expect-error
						event?.data?.message?.data,
					);

					handleAddCreditCard(paymentEvent);
				} catch (error) {
					console.log(error);
					setIsAddCreditCardIframeError(true);
				}
			}

			const errorEventTypes: EventTypes[] = [
				"spi_submit_decline",
				"spi_submit_error",
			];
			const errorMessage = getSPIframeErrorMessage(event);
			if (errorMessage) setCardError(errorMessage);
			else if (eventType.data === "spi_submit_declined") {
				setCardError("Please fill all the required fields");
			} else if (
				eventType.success &&
				errorEventTypes.includes(eventType.data)
			) {
				setIsAddCreditCardIframeError(true);
			}
		} catch (error) {
			console.log(error);
			// we dont care about errors here cause we only check for spi submit event .
		}
	}

	const handleSubmitPayment = async () => {
		const paymentDetails: PaymentDetails = {
			location_id: locationId.toString(),
			access_token: iframeAccessToken,
			process_type: "tokenize",
			type: "plan",
			storeCard: true,
			user_id: user.id.toString(),
			card_holder: `${user.first_name} ${user.last_name}`,
			currency_iso: "USD",
			country_iso: "US",
			phone_number: "",
			"include-street": 2,
			"include-street2": 1,
			"include-zip": 2,
			"include-email": 2,
			"include-country": 0,
			"include-city": 2,
			"include-state": 2,
			guest_id: user.id,
		};
		submitPaymentMutation.mutate(paymentDetails);
	};

	if (
		submitPaymentMutation.status === "pending" ||
		postCardMutation.status === "pending"
	) {
		return (
			<div className="flex flex-col items-center relative h-[calc(100dvh-4rem-5rem)]">
				<div className="text-center">
					<Typography className="mb-4">
						Verifying your card details...
					</Typography>
					<Typography variant={"p"}>
						Please wait while we protect your account from fraud.
					</Typography>
				</div>
				<div className="h-full  flex items-center justify-center  ">
					<WhiteLabelLogo />
				</div>
			</div>
		);
	}

	if (
		postCardMutation.status === "success" &&
		submitPaymentMutation.status === "success"
	) {
		return (
			<div className="pt-6">
				<CardSuccessPage
					onAddNewCard={() => {
						postCardMutation.reset();
						submitPaymentMutation.reset();
						removeIframe();
						setupIframe();
						setIsAddCreditCardIframeError(false);
						setCardError("");
						setLoadingIframe(true);
						setIframeHeight(DEFAULT_IFRAME_HEIGHT_PX);
					}}
				/>
			</div>
		);
	}

	if (
		postCardMutation.status === "error" ||
		submitPaymentMutation.status === "error" ||
		isAddCreditCardIframeError
	) {
		return (
			<div className="pt-6">
				<CardFailedPage
					errorMessage={cardError ?? postCardMutation.error?.message}
					onTryAgainClick={() => {
						window.location.reload();
					}}
				/>
			</div>
		);
	}

	const iframeLoading = loadingIframe && !isAddCreditCardIframeError;

	return (
		<>
			{iframeLoading ? (
				<div
					className={
						"w-full h-[calc(100dvh-4rem-5rem)] pb-20 flex items-center justify-center absolute"
					}
				>
					<WhiteLabelLogo />
				</div>
			) : (
				<></>
			)}
			<div>
				<div className="relative flex justify-center w-full pt-8">
					<Progress value={50} className="max-w-16" />
				</div>
				<Typography className="text-center text-2xl">Add a new card</Typography>
			</div>
			<div
				className="w-full relative"
				id="iframeContent"
				style={{ height: `${iframeHeight}px` }}
			>
				<style>{`
          #iframeContent iframe {
            height: 100% !important;
            width: 100% !important;
            display: block;
          }
        `}</style>
			</div>
			{cardError && (
				<p className="text-red-500 flex justify-center text-center w-full pb-2">
					{cardError}
				</p>
			)}
			<div>
				<p className="text-xs justify-center w-full  font-medium flex gap-2 items-center text-textLight mb-4 text-left">
					<LockIcon className="size-4" />
					{BANK_ACCOUNT_STATEMENT}
				</p>
				<SubmitButton
					onClick={handleSubmitPayment}
					type="button"
					disabled={loadingIframe}
					className={"w-full"}
					mutationStatus={postCardMutation.status}
				>
					Add Card
				</SubmitButton>
			</div>
		</>
	);
};

export default SoftpointIframe;
